import React, { useCallback, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '../../store';
import { markStepComplete } from '../../store/clubChecklistSlice';
import { setModalState, modalViewsConst } from '../../store/modalSlice';
import MiniLineChart from './MiniLineChart';
import MarkdownMessage from './MarkdownMessage';
import MiniMap from './MiniMap';
import GeoJsonMapLoader from './GeoJsonMapLoader';
import { extractChartsDataFromMessage } from './csvUtils';
import { extractGeoJSONFromMessage } from './geoJsonUtils';
import { extractImagesFromMessage, toPublicUrl } from './imageUtils';
import { extractGraphsFromMessage } from './graphUtils';
import GraphChart from './GraphChart';
import { stripModelScaffolding } from './textCleanup';
import AssetProposalGroup from '../AiChatComponent/AssetProposal/AssetProposalGroup';
import { AssetProposal } from '../AiChatComponent/hooks/assetProposalUtils';
import ModelDeployProposalGroup from '../AiChatComponent/ModelDeployProposal/ModelDeployProposalGroup';
import { ModelDeployProposal } from '../AiChatComponent/ModelDeployProposal/ModelDeployProposalCard';
import { downloadFileFromUrl, filenameFromUrl } from '../../utils/fileUtils';
import type { LightboxItem } from '../Modal/LightboxModal';

interface MessageWithChartProps {
  message: string;
  triggerText?: string;
  className?: string;
  conversationId?: string;
  messageId?: string;
  // When true, render in a read-only (e.g. shared/public) context: charts, maps,
  // images and CSV downloads still work, but asset-proposal action UI is hidden.
  readOnly?: boolean;
}

function parseY(raw: unknown): number | null {
  const parsed = Number(raw);
  return raw !== '' && raw != null && Number.isFinite(parsed) ? parsed : null;
}

// Color palette with distinct but brand-consistent colors
const CHART_COLORS = [
  '#7BA428', // Primary green (from your brand)
  '#000000', // Black for second line
  '#2563eb', // Blue from asset graphs
  '#dc2626', // Red from asset graphs
  '#f97316', // Orange from asset graphs
  '#7c3aed', // Purple from asset graphs
  '#0f766e', // Teal from asset graphs
  '#16a34a', // Green from asset graphs
];

const MessageWithChart: React.FC<MessageWithChartProps> = ({ message, triggerText, className = '', conversationId, messageId, readOnly = false }) => {
  const dispatch = useDispatch<AppDispatch>();
  // Strip leaked <thought> blocks / echoed directives before anything else, so both the artifact
  // extraction and the fallback render below operate on clean text.
  const cleanedMessage = useMemo(() => stripModelScaffolding(message), [message]);
  // Whole extraction chain memoized together so downstream lists keep a stable identity between renders
  const { extractedData, extractedMapData, extractedImages, extractedGraphs, displayText } = useMemo(() => {
    const data = extractChartsDataFromMessage(cleanedMessage);
    const mapData = extractGeoJSONFromMessage(data ? data.originalMessage : cleanedMessage);
    const afterMaps = mapData ? mapData.originalMessage : data ? data.originalMessage : cleanedMessage;
    const imagesData = extractImagesFromMessage(afterMaps, conversationId);
    const afterImages = imagesData ? imagesData.originalMessage : afterMaps;
    // Interactive <graph> charts (chart-spec JSON rendered via Chart.js). Coexists
    // with the inline <csv> → MiniLineChart path above.
    const graphs = extractGraphsFromMessage(afterImages);
    return {
      extractedData: data,
      extractedMapData: mapData,
      extractedImages: imagesData,
      extractedGraphs: graphs,
      // Clean text to display (csv, geojson, image, and graph blocks stripped)
      displayText: graphs ? graphs.originalMessage : afterImages,
    };
  }, [cleanedMessage, conversationId]);

  const { assetProposals, assetAddProposals, modelDeployProposals, finalDisplayText } = useMemo(() => {
    const proposals: AssetProposal[] = [];
    const proposalRegex = /<asset_proposal>([\s\S]*?)<\/asset_proposal>/g;
    let match: RegExpExecArray | null;
    while ((match = proposalRegex.exec(displayText)) !== null) {
      try {
        const parsed = JSON.parse(match[1]);
        if (parsed?.assetGroup && Array.isArray(parsed?.assets)) proposals.push(parsed);
      } catch { /* malformed block — skip */ }
    }
    const addProposals: AssetProposal[] = [];
    const addProposalRegex = /<asset_add_proposal>([\s\S]*?)<\/asset_add_proposal>/g;
    while ((match = addProposalRegex.exec(displayText)) !== null) {
      try {
        const parsed = JSON.parse(match[1]);
        if (typeof parsed.assetGroupName !== 'string') delete parsed.assetGroupName;
        if (parsed.assets && Array.isArray(parsed.assets)) addProposals.push(parsed);
      } catch { /* malformed block — skip */ }
    }
    const deployProposals: ModelDeployProposal[] = [];
    const deployProposalRegex = /<model_deploy_proposal>([\s\S]*?)<\/model_deploy_proposal>/g;
    while ((match = deployProposalRegex.exec(displayText)) !== null) {
      try {
        const parsed = JSON.parse(match[1]);
        if (parsed?.templateId) deployProposals.push(parsed);
      } catch { /* malformed block — skip */ }
    }
    return {
      assetProposals: proposals,
      assetAddProposals: addProposals,
      modelDeployProposals: deployProposals,
      finalDisplayText: displayText
        .replace(/<asset_proposal>[\s\S]*?<\/asset_proposal>/g, '')
        .replace(/<asset_add_proposal>[\s\S]*?<\/asset_add_proposal>/g, '')
        .replace(/<model_deploy_proposal>[\s\S]*?<\/model_deploy_proposal>/g, '')
        .trim(),
    };
  }, [displayText]);

  // Resolved once per message so the render pass and the lightbox share the same list.
  const images: LightboxItem[] = useMemo(
    () =>
      (extractedImages?.urls ?? []).map((url) => ({
        type: 'image' as const,
        url: toPublicUrl(url, conversationId),
        title: filenameFromUrl(url),
      })),
    [extractedImages, conversationId],
  );

  const openLightbox = useCallback(
    (initialIndex: number) => {
      dispatch(setModalState({
        modalOpen: true,
        modalView: modalViewsConst.lightbox,
        properties: { __modalSize: 'xl', items: images, initialIndex },
      }));
    },
    [dispatch, images],
  );

  // Only one card can show a failure at a time — a new attempt clears the previous message
  const [failedDownloadIndex, setFailedDownloadIndex] = useState<number | null>(null);

  const handleImageDownload = useCallback(async (index: number, url: string, filename: string) => {
    setFailedDownloadIndex(null);
    const ok = await downloadFileFromUrl(url, filename);
    if (!ok) setFailedDownloadIndex(index);
  }, []);

  if ((assetProposals.length > 0 || assetAddProposals.length > 0 || modelDeployProposals.length > 0) && !messageId) {
    console.warn('[MessageWithChart] Proposals found but no messageId provided — proposal cards will not render.');
  }

  // If no chart, map, image, graph, or proposal data found, return original message
  if (!extractedData && !extractedMapData && !extractedImages && !extractedGraphs && assetProposals.length === 0 && assetAddProposals.length === 0 && modelDeployProposals.length === 0) {
    return (
      <MarkdownMessage content={cleanedMessage} className={className} />
    );
  }

  const charts = extractedData ? extractedData.charts : [];

  return (
    <div className={className}>
      {/* Original message text (without CSV, GeoJSON, image, or asset_proposal blocks) */}
      {finalDisplayText && (
        <MarkdownMessage content={finalDisplayText} className="mb-3" />
      )}
      
      {charts.map((chart, chartIndex) => {
        const { chartData, csvString, title } = chart;

        const handleDownload = () => {
          const blob = new Blob([csvString], { type: 'text/csv' });
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          const safeTitle = (title || `chart-${chartIndex + 1}`).replace(/\s+/g, '-').toLowerCase();
          a.download = `${safeTitle}.csv`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          window.URL.revokeObjectURL(url);
          dispatch(markStepComplete(3));
        };

        const lines = chartData.yColumns.map((column, index) => {
          const axis: 'primary' | 'secondary' = chartData.axisGroups?.secondary.includes(column) ? 'secondary' : 'primary';
          return {
            name: column,
            color: CHART_COLORS[index % CHART_COLORS.length],
            axis,
            data: chartData.data.map((row, rowIndex) => ({
              x: row[chartData.xColumn] || rowIndex,
              y: parseY(row[column])
            }))
          };
        });

        return (
          <div key={chartIndex} className="bg-gray-50 rounded-lg py-4 px-3 border border-gray-200 relative mb-4 last:mb-0">
            {title && (
              <div className="mb-3">
                <h4 className="text-sm font-medium text-gray-700 truncate">{title}</h4>
              </div>
            )}

            <div className="mb-3">
              <div className="flex flex-wrap gap-x-4 gap-y-1">
                {lines.map((line, index) => (
                  <div key={index} className="flex items-center space-x-1 min-w-0">
                    <div 
                      className="w-3 h-3 rounded-full flex-shrink-0"
                      style={{ backgroundColor: line.color }}
                    />
                    <span className="text-xs text-gray-600 truncate">
                      {line.name}
                      {line.axis === 'secondary' && (
                        <span className="text-gray-400 ml-1">(R)</span>
                      )}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            <MiniLineChart 
              lines={lines}
              height={220}
              className="w-full rounded"
            />

            <button
              onClick={handleDownload}
              className="absolute bottom-2 right-3 w-8 h-8 bg-white/90 backdrop-blur-sm border border-gray-300/70 rounded-md shadow-sm hover:bg-white hover:border-gray-400 transition-colors flex items-center justify-center group"
              title="Download CSV data"
            >
              <svg 
                className="w-4 h-4 text-gray-500 group-hover:text-gray-700" 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth={2} 
                  d="M19 14l-7 7m0 0l-7-7m7 7V3" 
                />
              </svg>
            </button>

            <div className="mt-3 text-xs text-gray-500 text-center">
              {chartData.data.length} data points • {chartData.yColumns.length} series
            </div>
          </div>
        );
      })}

      {extractedMapData && extractedMapData.maps.map((mapItem, mapIndex) =>
        mapItem.src ? (
          // Reference form (<geojson src>): geometry fetched from the
          // conversation's output/ prefix — see GeoJsonMapLoader.
          <GeoJsonMapLoader
            key={`map-${mapIndex}`}
            src={mapItem.src}
            title={mapItem.title}
            conversationId={conversationId}
            fixedHeight={420}
          />
        ) : mapItem.geojson ? (
          <MiniMap
            key={`map-${mapIndex}`}
            geojson={mapItem.geojson}
            title={mapItem.title}
            fixedHeight={420}
          />
        ) : mapItem.error ? (
          <div
            key={`map-${mapIndex}`}
            role="status"
            className="mt-3 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800"
          >
            {mapItem.title ? <span className="font-medium">{mapItem.title}: </span> : null}
            {mapItem.error}
          </div>
        ) : null
      )}

      {extractedGraphs && extractedGraphs.paths.map((graphPath, graphIndex) => (
        <GraphChart
          key={`graph-${graphIndex}`}
          path={graphPath}
          conversationId={conversationId}
          readOnly={readOnly}
        />
      ))}

      {images.map(({ url: fullUrl, title: filename }, i) => (
        <div key={`img-${i}`} className="bg-gray-50 rounded-md border border-gray-200 p-5 mt-3 float-left mr-4 mb-4 relative">
          <button
            type="button"
            onClick={() => openLightbox(i)}
            aria-label={`View ${filename} full screen`}
            className="block w-full rounded-md overflow-hidden cursor-pointer hover:opacity-90 active:opacity-75 transition-opacity"
          >
            <img
              src={fullUrl}
              alt={filename}
              className="w-full object-contain max-h-[200px]"
            />
          </button>
          <button
            onClick={() => handleImageDownload(i, fullUrl, filename)}
            className="absolute bottom-2 right-3 w-8 h-8 bg-white/90 backdrop-blur-sm border border-gray-300/70 rounded-md shadow-sm hover:bg-white hover:border-gray-400 active:bg-gray-100 transition-colors flex items-center justify-center group"
            title="Download image"
          >
            <svg
              className="w-4 h-4 text-gray-500 group-hover:text-gray-700"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 14l-7 7m0 0l-7-7m7 7V3"
              />
            </svg>
          </button>
          {failedDownloadIndex === i && (
            <p role="alert" className="mt-2 text-xs text-red-600">Download failed — try again.</p>
          )}
        </div>
      ))}

      {/* Asset-proposal action UI is hidden in read-only (shared/public) contexts */}
      {!readOnly && assetProposals.length > 0 && messageId && conversationId && (
        <AssetProposalGroup proposals={assetProposals} conversationId={conversationId} messageId={messageId} isAddMode={false} />
      )}

      {!readOnly && assetAddProposals.length > 0 && messageId && conversationId && (
        <AssetProposalGroup proposals={assetAddProposals} conversationId={conversationId} messageId={`${messageId}-add`} isAddMode={true} />
      )}

      {/* Deploy-model proposal cards — hidden in read-only (shared/public) contexts */}
      {!readOnly && modelDeployProposals.length > 0 && messageId && conversationId && (
        <ModelDeployProposalGroup
          proposals={modelDeployProposals}
          conversationId={conversationId}
          messageId={messageId}
        />
      )}
    </div>
  );
};

export default MessageWithChart;